import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import VeyraLogo from "@/components/brand/VeyraLogo";
import PasswordInput from "@/components/common/PasswordInput";
import { api } from "@/lib/api";
import { t } from "@/i18n";

const ADMIN_CMD = "python backend/admin_recovery.py";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-sm font-medium">{label}</span>
    {children}
  </label>
);

/**
 * RecoveryPage — restablecimiento de contraseña mediante token administrativo.
 *
 * El propietario debe ejecutar `admin_recovery.py` en el servidor para obtener
 * el código. La pantalla explica esto explícitamente y nunca promete un correo.
 */
const RecoveryPage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ token: "", password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (form.password.length < 12) {
      setError(t.auth.newPasswordTooShort);
      return;
    }
    if (form.password !== form.confirm) {
      setError(t.auth.changePasswordMismatch);
      return;
    }
    setBusy(true);
    try {
      await api.useRecoveryToken({ token: form.token.trim(), new_password: form.password });
      toast.success(t.auth.recoverySuccess);
      navigate("/login");
    } catch (err) {
      setError(t.auth[err.message] || t.auth.generic);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="recovery-page" className="flex min-h-screen items-center justify-center bg-[#101010] px-4 text-[#f4f1ec]">
      <div className="w-full max-w-md v-fade-up">
        <VeyraLogo size={30} className="mb-8" />
        <h1 className="text-2xl font-semibold tracking-tight">{t.auth.recoveryTitle}</h1>
        <p className="mt-2 text-sm text-[#a3a3a3]">{t.auth.recoverySubtitle}</p>

        {/* Instrucción administrativa */}
        <div
          data-testid="recovery-admin-instruction"
          className="mt-6 rounded-lg border border-[#303030] bg-[#151515] p-4"
        >
          <p className="mb-2 text-xs font-semibold text-[#a3a3a3] uppercase tracking-wider">
            {t.auth.recoveryAdminCmd}
          </p>
          <code
            data-testid="recovery-admin-cmd"
            className="block rounded bg-[#0d0d0d] px-3 py-2 font-mono text-sm text-[#f5b544] select-all"
          >
            {ADMIN_CMD}
          </code>
          <p className="mt-2 text-xs text-[#6b6b6b]">{t.auth.recoveryAdminNote}</p>
        </div>

        <form onSubmit={submit} className="mt-6 flex flex-col gap-4">
          <Field label={t.auth.recoveryToken}>
            <input
              data-testid="recovery-token-input"
              required
              type="text"
              autoComplete="off"
              autoFocus
              className="v-input font-mono"
              placeholder={t.auth.recoveryTokenPh}
              value={form.token}
              onChange={set("token")}
            />
          </Field>
          <Field label={t.auth.recoveryNewPassword}>
            <PasswordInput
              testId="recovery-password-input"
              required
              autoComplete="new-password"
              placeholder={t.auth.newPasswordHint}
              value={form.password}
              onChange={set("password")}
            />
          </Field>
          <Field label={t.auth.recoveryConfirmPassword}>
            <PasswordInput
              testId="recovery-confirm-input"
              required
              autoComplete="new-password"
              placeholder={t.auth.recoveryConfirmPassword}
              value={form.confirm}
              onChange={set("confirm")}
            />
          </Field>
          {error && (
            <p data-testid="recovery-error" className="text-sm text-[#e5655a]">{error}</p>
          )}
          <button
            data-testid="recovery-submit"
            type="submit"
            disabled={busy || !form.token || !form.password || !form.confirm}
            className="v-btn-primary justify-center"
          >
            {busy ? t.auth.recoveryBusy : t.auth.recoveryButton}
          </button>
        </form>

        <Link
          data-testid="recovery-back-link"
          to="/login"
          className="mt-6 block text-center text-xs text-[#6b6b6b] underline-offset-2 hover:underline"
        >
          {t.auth.recoveryBackToLogin}
        </Link>
      </div>
    </div>
  );
};

export default RecoveryPage;
